import React, { useState, useEffect } from 'react';
import '../Styles/home.css';
import { Link } from 'react-router-dom';
import SERVER from '../server.js'; 

const activeHomeIcon = `${SERVER}/img/home-active.png`;
const unactiveWishlistIcon = `${SERVER}/img/wishlist-unactive.png`;
const activeWishlistIcon = `${SERVER}/img/wishlist-active.png`;
const uncartIcon = `${SERVER}/img/cart-unactive.png`;
const activeCartIcon = `${SERVER}/img/cart-active.png`;
const unactiveFriendsIcon = `${SERVER}/img/friends-unactive.png`;
const errorimg = `${SERVER}/img/error.png`;

// Notification Component
const Notification = ({ message, type, visible, onClose }) => {
  useEffect(() => {
    if (visible) {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }
  }, [visible, onClose]);

  if (!visible) return null;

  return (
    <div className={`notification ${type}`}>
      <span>{message}</span>
      <button onClick={onClose} className="notification-close">×</button>
    </div>
  );
};

function Home() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [user, setUser] = useState(null);
  const [isWishlistLoading, setIsWishlistLoading] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [notification, setNotification] = useState({ message: '', type: '', visible: false });
  const [cartItemCount, setCartItemCount] = useState(0);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
 
  // Show notification helper
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type, visible: true });
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, visible: false }));
  };

  useEffect(() => {
    const initUser = async () => {
      if (window.Telegram && window.Telegram.WebApp) {
        const tg = window.Telegram.WebApp;
        const userData = tg.initDataUnsafe?.user;
        
        if (userData) {
          const userInfo = {
            id: userData.id,
            firstName: userData.first_name,
            lastName: userData.last_name,
            username: userData.username,
            languageCode: userData.language_code,
            isPremium: userData.is_premium
          };
          
          setUser(userInfo);
          await fetchUserData(userData.id);
        } else {
          setError('Telegram user data not available. Please open this app through Telegram.');
          setLoading(false);
        }
      } else {
        setError('This app can only be used through Telegram. Please open it via Telegram Web App.');
        setLoading(false);
      }
    };

    const fetchProducts = async () => {
      try {
        const response = await fetch(`${SERVER}/products`);
        if (!response.ok) throw new Error('Network response was not ok');
        const data = await response.json();
        setProducts(data);
        setFilteredProducts(data);
      } catch (err) {
        setError(err.message);
        showNotification('Failed to load products. Please try again.', 'error');
      } finally {
        setLoading(false);
      }
    };

    initUser();
    fetchProducts();
  }, []);

  // Filter and sort products
  useEffect(() => {
    let filtered = products.filter(product =>
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort products
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return a.price - b.price;
        case 'price-high':
          return b.price - a.price;
        case 'name':
        default:
          return a.name.localeCompare(b.name);
      }
    });

    setFilteredProducts(filtered);
  }, [products, searchTerm, sortBy]);

  const fetchUserData = async (userId) => {
    try {
      // Fetch wishlist
      const wishlistResponse = await fetch(`${SERVER}/users/${userId}/wishlist`);
      if (wishlistResponse.ok) {
        const wishlistData = await wishlistResponse.json();
        const wishlistIds = Array.isArray(wishlistData) 
          ? wishlistData.map(item => item.product_id)
          : [];
        setWishlistItems(wishlistIds);
      }
      
      // Fetch cart
      const cartResponse = await fetch(`${SERVER}/users/${userId}/cart`);
      if (cartResponse.ok) {
        const cartData = await cartResponse.json();
        const cartIds = cartData.items 
          ? cartData.items.map(item => item.product_id)
          : [];
        setCartItems(cartIds);
        setCartItemCount(cartData.items ? cartData.items.length : 0);
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
    }
  };

  const handleProductView = (productId) => {
    setRecentlyViewed(prev => {
      const updated = [productId, ...prev.filter(id => id !== productId)].slice(0, 5);
      return updated;
    });
  };

  const toggleWishlistItem = async (productId) => {
    if (!user?.id) {
      showNotification('Please log in to add items to your wishlist', 'error');
      return;
    }

    setIsWishlistLoading(prev => ({ ...prev, [productId]: true }));

    try {
      if (wishlistItems.includes(productId)) {
        // Remove from wishlist
        const response = await fetch(`${SERVER}/users/${user.id}/wishlist/${productId}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          setWishlistItems(prev => prev.filter(id => id !== productId));
          showNotification('Item removed from wishlist', 'success');
        } else {
          const data = await response.json();
          showNotification('Failed to remove from wishlist', 'error');
          console.error('Error removing from wishlist:', data.message || data);
        }
      } else {
        // Add to wishlist
        const response = await fetch(`${SERVER}/users/${user.id}/wishlist`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ product_id: productId })
        });

        if (response.ok) {
          setWishlistItems(prev => [...prev, productId]);
          showNotification('Item added to wishlist! ❤️', 'success');
        } else {
          const data = await response.json();
          showNotification('Failed to add to wishlist', 'error');
          console.error('Error adding to wishlist:', data.message || data);
        }
      }
    } catch (e) {
      showNotification('Network error. Please check your connection.', 'error');
      console.error('Request failed:', e);
    } finally {
      setIsWishlistLoading(prev => ({ ...prev, [productId]: false }));
    }
  };

  const toggleCartItem = async (productId) => {
    if (!user?.id) {
      showNotification('Please log in to add items to your cart', 'error');
      return;
    }
    
    try {
      if (cartItems.includes(productId)) {
        // Remove from cart
        const response = await fetch(`${SERVER}/users/${user.id}/cart/${productId}`, {
          method: 'DELETE'
        });
        
        if (response.ok) {
          setCartItems(prev => prev.filter(id => id !== productId));
          setCartItemCount(prev => Math.max(0, prev - 1));
          showNotification('Item removed from cart', 'success');
        } else {
          showNotification('Failed to remove from cart', 'error');
        }
      } else {
        // Add to cart
        const response = await fetch(`${SERVER}/users/${user.id}/cart`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ product_id: productId, quantity: 1 })
        });
        
        if (response.ok) {
          setCartItems(prev => [...prev, productId]);
          setCartItemCount(prev => prev + 1);
          showNotification('Item added to cart! 🛒', 'success');
        } else {
          showNotification('Failed to add to cart', 'error');
        }
      }
    } catch (err) {
      showNotification('Network error. Please check your connection.', 'error');
      console.error('Error updating cart:', err);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  if (loading) {
    return (
      <div className="home-container">
        <div className="loading">
          <div className="loading-spinner"></div>
          <div>Finding the best products for you...</div>
          <small>This won't take long</small>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="home-container">
        <div className="error">
          <div className="error-icon">⚠️</div>
          <strong>We're experiencing some technical difficulties</strong>
          <br />
          {error}
          <br />
          <div className="error-actions">
            <button onClick={() => window.location.reload()} className="retry-btn">
              Try Again
            </button>
            <Link to="/cart" className="view-cart-btn">
              View Cart Instead
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="home-container">
      <Notification
        message={notification.message}
        type={notification.type}
        visible={notification.visible}
        onClose={closeNotification}
      />
      {/* Affiliate Program */}
      <section className="affiliate-section">
        <div className="right-content">
          <h1>AFFILIATE PROGRAM</h1>
          <p>Earn money by inviting friends!</p>
          <Link to="/friends">
            <button className="learn-more-btn">LEARN MORE</button>
          </Link>
        </div>
        <span className="emoji">🤝</span>
      </section>

      {/* Search and Filter Section */}
      <div className="search-filter-row">
        <div className="search-container">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button onClick={clearSearch} className="clear-search">×</button>
          )}
        </div>
        <div className="sort-container">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-select"
          >
            <option value="name">Sort by Name</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
          </select>
        </div>
      </div>


      {/* Products Results Info */}
      {searchTerm && (
        <div className="search-results-info">
          <p>Found {filteredProducts.length} products for "{searchTerm}"</p>
          {filteredProducts.length === 0 && (
            <div className="no-results">
              <p>No products found. Try a different search term.</p>
              <button onClick={clearSearch} className="clear-search-btn">
                Clear Search
              </button>
            </div>
          )}
        </div>
      )}

      {/* Products */}
      <div className="products-grid">
        {filteredProducts.map((product) => (
          <div 
            key={product.id} 
            className="product-card"
            onClick={() => handleProductView(product.id)}
          >
            <div className="product-image-container">
              <img 
                src={product.photo_path ? `${SERVER}/img/${product.photo_path}` : errorimg}
                alt={product.name} 
                className="product-image" 
                onError={(e) => {
                  e.target.src = errorimg;
                }}
              />
              <button 
                className={`wishlist-button ${wishlistItems.includes(product.id) ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  !isWishlistLoading[product.id] && toggleWishlistItem(product.id);
                }}
                disabled={isWishlistLoading[product.id]}
                title={wishlistItems.includes(product.id) ? 'Remove from wishlist' : 'Add to wishlist'}
              >
                {isWishlistLoading[product.id] ? (
                  <span className="loading-dot">⋯</span>
                ) : (
                  <img 
                    src={wishlistItems.includes(product.id) ? activeWishlistIcon : unactiveWishlistIcon} 
                    alt="wishlist"
                  />
                )}
              </button>
              {product.discount && (
                <div className="discount-badge">-{product.discount}%</div>
              )}
            </div>
            <div className="product-details">
              <div className="product-info">
                <h3 title={product.name}>{product.name}</h3>
                <p className="product-price">${product.price.toFixed(2)} / 1 UNIT</p>
                {product.originalPrice && (
                  <p className="original-price">${product.originalPrice.toFixed(2)}</p>
                )}
              </div>
              <button
                className={`cart-button ${cartItems.includes(product.id) ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleCartItem(product.id);
                }}
                title={cartItems.includes(product.id) ? 'Remove from cart' : 'Add to cart'}
              >
                <img
                  src={cartItems.includes(product.id) ? activeCartIcon : uncartIcon}
                  alt="add to cart"
                />
              </button>
            </div>
            <div className="product-rating">
              <span className="stars">⭐⭐⭐⭐⭐</span>
              <span className="rating-text">(5.0)</span>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation */}
      <nav className="bottom-nav">
        <Link to="/" className="nav-btn active">
          <img src={activeHomeIcon} alt="Home" className="nav-icon" />
          <span>Home</span>
        </Link>
        <Link to="/Wishlist" className="nav-btn">
          <img src={unactiveWishlistIcon} alt="Wishlist" className="nav-icon" />
          <span>Wishes</span>
          {wishlistItems.length > 0 && (
            <span className="nav-badge">{wishlistItems.length}</span>
          )}
        </Link>
        <Link to="/Cart" className="nav-btn">
          <img src={uncartIcon} alt="Cart" className="nav-icon" />
          <span>Cart</span>
          {cartItemCount > 0 && (
            <span className="nav-badge">{cartItemCount}</span>
          )}
        </Link>
        <Link to="/Friends" className="nav-btn">
          <img src={unactiveFriendsIcon} alt="Friends" className="nav-icon" />
          <span>Friends</span>
        </Link>
      </nav>
    </div>
  );
}

export default Home;